import { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import { postLogoutUrl } from "../lib/hostScope";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking

  useEffect(() => {
    // Portal Access Center: 1-click impersonation launch URL pattern
    // `…/dashboard?_impersonate=<jwt>` — strip the token, save it, reload clean.
    try {
      const url = new URL(window.location.href);
      const imp = url.searchParams.get("_impersonate");
      if (imp) {
        localStorage.setItem("token", imp);
        url.searchParams.delete("_impersonate");
        window.history.replaceState({}, "", url.toString());
      }
    } catch { /* ignore */ }

    const t = localStorage.getItem("token");
    // Always try /auth/me — the cookie (from a sibling subdomain login) may
    // authenticate us even when localStorage is empty. Only fall back to
    // "logged out" when both paths fail.
    api.get("/auth/me")
      .then((r) => {
        // 200 with a null body = anonymous/logged-out visitor.
        if (!r.data) {
          if (t) localStorage.removeItem("token");
          setUser(null);
        } else {
          setUser(r.data);
        }
      })
      .catch(() => {
        if (t) localStorage.removeItem("token");
        setUser(null);
      });
  }, []);

  const login = async (email, password, totp_code) => {
    let data;
    try {
      const r = await api.post("/auth/login", {
        email, password, portal: "main",
        ...(totp_code ? { totp_code } : {}),
      });
      data = r.data;
    } catch (e) {
      // TOTP-required (any role with TOTP enrolled) — surface as a
      // structured "challenge" instead of an error so Login.jsx can
      // show the 6-digit input without dumping the user out.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 401 && (detail?.code === "totp_required" || detail === "Admin 2FA required. Provide your authenticator code." || detail === "Two-factor authentication required.")) {
        return { requires_totp: true };
      }
      throw e;
    }
    // SMS-MFA challenge: backend will not issue a token until the user
    // submits the OTP code. We immediately fire `/sms/login/challenge`
    // here so the SMS hits the user's phone the moment the SMS prompt
    // appears — they don't have to click anything extra to receive it.
    // We bubble up the challenge_token so the SMS-verify step can use
    // it WITHOUT re-firing a second SMS.
    if (data.requires_sms_mfa) {
      try {
        const { data: ch } = await api.post("/sms/login/challenge", { email, password });
        return {
          ...data,
          challenge_token: ch.challenge_token || null,
          masked: ch.masked || data.masked_phone || "",
        };
      } catch (e) {
        // Challenge couldn't fire (Twilio down, etc.) — fall through and
        // let the manual SMS step retry. UI still shows the SMS prompt.
        return data;
      }
    }
    if (data.token) localStorage.setItem("token", data.token);
    setUser(data);
    return data;
  };

  const verifySmsLogin = async (email, password, code, challengeToken = null) => {
    // Prefer the challenge_token we already have from login() — that way
    // we don't fire a SECOND SMS to the user every time they submit. If
    // it's somehow missing (page refresh after the prompt), we re-issue.
    let tok = challengeToken;
    if (!tok) {
      const { data: ch } = await api.post("/sms/login/challenge", { email, password });
      if (!ch.requires_sms_mfa) {
        // Phone got un-verified between attempts — fall back to plain login.
        return login(email, password);
      }
      tok = ch.challenge_token;
    }
    const { data } = await api.post("/sms/login/verify", {
      challenge_token: tok, code,
    });
    if (data.token) localStorage.setItem("token", data.token);
    setUser(data);
    return data;
  };

  const register = async (payload, opts = {}) => {
    // Optional `bypassKey` lets internal team bypass the public-signup gate.
    // Appended as ?key=… so the backend's `is_signup_allowed(...)` check
    // recognises it.
    const url = opts.bypassKey ? `/auth/register?key=${encodeURIComponent(opts.bypassKey)}`
                                : "/auth/register";
    const { data } = await api.post(url, payload);
    if (data.token) localStorage.setItem("token", data.token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("token");
    // Clear the /demo session flag so logging out of a demo session takes
    // you back to the production view.
    sessionStorage.removeItem("app_mode_override");
    setUser(null);
    // Route the user to the canonical login. In prod, signing out from a
    // portal subdomain (buyers./admin./ops.) bounces to verqo.com/login —
    // there's nothing useful on a portal host once the session is gone.
    // In preview / single-host dev, this is just a relative /login redirect.
    const url = postLogoutUrl();
    if (url.startsWith("http://") || url.startsWith("https://")) {
      window.location.href = url;
    } else {
      window.location.assign(url);
    }
  };

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch { /* ignore */ }
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, login, register, logout, refresh, verifySmsLogin }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
